import _ from 'lodash';
import { CreateGenericChunkedUploader } from './GenericChunkedUploader';
import { addIconsToFiles, getRequestWithFiles } from './mappers';
import { canSaveField } from './formUtils';
import { RequestFileDto } from '../Schemas/RequestFileDto';
import { ScanAttachmentWithAIOutput } from '../Schemas/ScanAttachmentWithAIOutput';
import { ScanAttachmentWithAIInputSchema } from '../Schemas/ScanAttachmentWithAIInput';
import { RequestFormContext } from '../RequestFormContext';
import { flatFiles } from './file';
import { TaxonomyDto } from '../Schemas/TaxonomyDto';

export type ExtendedFile = File & {
    id?: string;
    extension?: string;
    uploadCompleted?: boolean;
    iconTooltip?: string;
    icon?: string;
    iconText?: string;
    taxonomy?: TaxonomyDto;
    requestId?: string;
    requestTypeId?: number;
};

export type ExtendedFileList = {
    [Symbol.iterator](): IterableIterator<ExtendedFile>;
}

export function uploadLogic(
    filesInput: FileList[] | File[],
    context: Omit<RequestFormContext, "pathToProp">,
    uploader: ReturnType<typeof CreateGenericChunkedUploader>,
    targetTaxonomy?: TaxonomyDto
) {
    const { openSnackbar, saveTask, settings } = context;

    const getCurrentRequest = () => context.request.current;

    let filesToRead: File[] = flatFiles(filesInput);

    const requestWithFiles = getRequestWithFiles(context.request);

    const existingFiles = filesToRead.filter((file: File) => {
        return requestWithFiles.current.files
            .filter((exFile: RequestFileDto) => {
                return exFile.name?.toLowerCase() === file.name.toLowerCase();
            }).length > 0;
    });

    // Filter files, only get files that don't exist already
    filesToRead = filesToRead.filter((file: File) => {
        const hasMatch = requestWithFiles.current.files.filter((exFile: RequestFileDto) => {
            return exFile.name?.toLowerCase() === file.name.toLowerCase();
        }).length > 0;
        return !hasMatch;
    });

    /// Display message if files already existed
    if (existingFiles.length > 0) {
        const moreThanOneFile = existingFiles.length > 1;

        let message = moreThanOneFile ? 'Súbory: ' : 'Súbor: ';

        for (let q = 0; q < existingFiles.length; q++) {
            message += existingFiles[q].name + ' ';
        }

        message += moreThanOneFile ? 'boli pripojené k žiadosti' : 'bol pripojený k žiadosti';

        openSnackbar({ text: message });
    }

    if (filesToRead.length > 0) {
        /// New request
        if (!getCurrentRequest().requestId) {
            const fieldsToSave = getCurrentRequest().fields.filter((fld) => canSaveField(fld));

            if (fieldsToSave.length > 0) {
                saveTask.debouncedPerform({
                    successHandler: () => {
                        performUpload(filesToRead);
                    },
                    errorHandler: () => {
                        if (getCurrentRequest().requestId) {
                            performUpload(filesToRead);
                        }
                    },
                    uploadHandler: async () => {
                        performUpload(filesToRead);
                    }
                });
            } else {
                openSnackbar({ text: "Bohužiaľ nebolo možné uložiť formulár, prosím upravte formulár a skúste to znova." });
            }
        }
        /// Request already existed or was saved
        else {

            performUpload(filesToRead);
        }
    }

    async function performUpload(filesToRead: ExtendedFile[]) {

        const newFilesToUpload: ExtendedFile[] = filesToRead.filter((item) => "slice" in item && !!item.slice);

        settings.current.uploadProcesses.push(true);
        context.rerenderForm();

        const requestWithFiles = getRequestWithFiles(context.request);

        addIconsToFiles(newFilesToUpload, false, false);

        const categorizedFilesToRead: ExtendedFile[] = newFilesToUpload.map((file) => {
            const mappedFile: ExtendedFile = file;
            if (targetTaxonomy) {
                const taxonomy = _.cloneDeep(targetTaxonomy);
                delete taxonomy.items;
                mappedFile.taxonomy = taxonomy;
                mappedFile.taxonomyId = taxonomy.id;
            }
            return mappedFile;
        });

        for (let i = 0; i < categorizedFilesToRead.length; i++) {
            (categorizedFilesToRead[i]).requestId = getCurrentRequest().requestId;
            (categorizedFilesToRead[i]).requestTypeId = getCurrentRequest().id;
            requestWithFiles.current.files.push(categorizedFilesToRead[i]);
        }

        context.rerenderForm();

        context.uploadInProgressNotifierTask.debouncedPerform();

        // Logic
        await uploader.UploadFileFunction(categorizedFilesToRead);

        for (let fi = 0; fi < requestWithFiles.current.files.length; fi++) {
            let file = requestWithFiles.current.files[fi];
            // Change the file object to normal object after upload
            if (categorizedFilesToRead.filter((ftr) => ftr.id === file.id).length > 0) {
                file = _.mergeWith({}, file, _.clone);
                requestWithFiles.current.files[fi] = file;
            }
        }

        // Unlock AI-gated steps after any successful upload batch (matches reload when files exist).
        // Do not depend on defaultDocumentTaxonomyId — that field is often missing on new drafts from getAllRequestsTypes.
        if (
            settings.current.requestHasAiDetection &&
            categorizedFilesToRead.some((item) => item.uploadCompleted === true)
        ) {
            settings.current.isSuccessfulUpload = true;
        }

        // Call the AI for the successfully uploaded files
        const promises = categorizedFilesToRead.map((item) => {
            if (item.uploadCompleted && getCurrentRequest().defaultDocumentTaxonomyId) {
                return new Promise((aiDefer) => {
                    ScanAttachmentWithAI(
                        item.id,
                        item.extension,
                        getCurrentRequest().id,
                        aiDefer,
                        context,
                        fillFieldsFromAiResult,
                    );
                });
            }
        });

        try {
            await Promise.all(promises);
            saveAfterUpload(context, categorizedFilesToRead);
        } catch {
            saveAfterUpload(context, categorizedFilesToRead);
        }
    }
}

async function fillFieldsFromAiResult(
    aiResult: ScanAttachmentWithAIOutput,
    originalFileId: string | undefined | null,
    defer: (value?: unknown) => void,
    context: Omit<RequestFormContext, "pathToProp">,
) {
    const result = aiResult.fieldsToFillOut;
    const getCurrentRequest = () => context.request.current;

    if (result) {
        // Loop through form fields, fill them via uiHint
        getCurrentRequest().fields.forEach(function (item) {
            if (result && result[item.uiHint!]) {
                item.paramValue = result[item.uiHint!];
                context.resetField(item.id!, { defaultValue: item.paramValue });
            }
        });
    }

    if (aiResult.files) {
        const requestWithFiles = getRequestWithFiles(context.request);

        getCurrentRequest().files = requestWithFiles.current.files.filter(function (file) {
            return file.id !== originalFileId;
        });

        aiResult.files.forEach(function (item) {
            item.uploadCompleted = true;
            item.icon = 'photo';
            if (item.extension) {
                item.name += '.' + item.extension;
            }
            item.iconTooltip = item.name;
            context.request.current.files?.push(item);
        });
    }

    context.rerenderForm();

    defer();
}

async function ScanAttachmentWithAI(
    fileId: string | undefined | null,
    _fileExtension: string | undefined | null,
    requestTypeId: number,
    deferred: (value: unknown) => void,
    context: Omit<RequestFormContext, "pathToProp">,
    successHandler: (
        aiResult: ScanAttachmentWithAIOutput,
        originalFileId: string | undefined | null,
        defer: (value: unknown) => void,
        context: Omit<RequestFormContext, "pathToProp">,
    ) => Promise<void>,
) {
    const input = ScanAttachmentWithAIInputSchema.parse({
        fileId: fileId,
        requestTypeId: requestTypeId,
    });

    const result = await context.requestApi.scanAttachmentWithAI(input);

    if (result && successHandler) {
        successHandler(result, fileId, deferred, context);
    }
}

function saveAfterUpload(context: Omit<RequestFormContext, "pathToProp">, files: ExtendedFile[]) {
    // Delete from upload process collection
    const uploadProcesses = context.settings.current.uploadProcesses
    uploadProcesses.splice(0, 1);

    context.settings.current.showFileLoading = false;
    context.rerenderForm();

    context.onlySaveRequest(undefined, files.some(f => (f.taxonomy ?? {}).isRequestDocument === true), () => context.uploadInCompletedNotifierTask.debouncedPerform());
}
